﻿import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { createContext, useMemo, useState, useEffect, useCallback, ReactNode } from "react";
import { useTokenActions } from "../shared/hooks/use-token-actions";
import { useRoutesProvider } from "./provider-hooks";
import { toastOneInfo } from "../toast";
import { jwtDecode } from "jwt-decode";
import type { AuthProviderValue, UserInfo } from './auth-provider.types';
import {
    TOKEN_KEY,
    REFRESH_TOKEN_KEY,
    TOKEN_EXPIRES_AT_KEY,
} from "../constants/config.ts";
import { setAuthenticatedUser, clearAuthenticatedUser } from "@/telemetry/app-insights";
import { identifyPostHogUser, resetPostHogUser } from "@/telemetry/posthog";
import { trackTeacherApprovalSeen } from "@/telemetry/posthog-events";
import { resolvePostAuthTarget } from "@/domains/auth/utils/resolve-post-auth-target";
import { pasiInscriereActive } from "@/domains/auth/utils/pasi-inscriere";
import { isPathAllowedForRole } from "@/domains/auth/utils/is-path-allowed";
import { onSessionExpired } from "@/core/auth/session-expired";
import { refreshAccessToken } from "@/core/api/client";
import { salveazaTokenDeAcces } from "@/core/auth/stocare-token";

interface AuthProviderV2Props {
    children: ReactNode;
}

/**
 * `teacher_approval_seen` o singura data per (profesor, editie). Pe `schoolApprovedForCurrentEdition`,
 * nu pe `isApproved`: acela e deja `true` pentru profesorul care revine, inainte de re-aprobare.
 * `localStorage` poate arunca (mod privat, cota plina) — atunci evenimentul se sare, nu se repeta.
 */
function marcheazaAprobareaVazuta(userId: string, editionId: string | undefined): void {
    const cheie = `ph_teacher_approval_seen:${userId}:${editionId ?? ''}`;
    try {
        if (localStorage.getItem(cheie)) return;
        localStorage.setItem(cheie, '1');
    } catch {
        return;
    }
    trackTeacherApprovalSeen();
}

const AuthProviderContextV2 = createContext<AuthProviderValue | null>(null);
const { Provider } = AuthProviderContextV2;

export default function AuthProviderV2({ children }: AuthProviderV2Props): JSX.Element {
    const { isTokenValid, isTokenExpired, retrieveTokenFromStorage, setTokenInStorage } = useTokenActions();
    const [token, setToken] = useState(retrieveTokenFromStorage(TOKEN_KEY));
    const [refreshToken, setRefreshToken] = useState(retrieveTokenFromStorage(REFRESH_TOKEN_KEY));

    // Decode JWT token with validation logging
    const userInfo = useMemo<UserInfo | null>(() => {
        console.log('=== USERINFO MEMO RECALCULATING ===');
        console.log('[Auth] Token state changed, recalculating userInfo');
        console.log('[Auth] Current token (first 50 chars):', token?.substring(0, 50));

        if (!token) {
            console.log('[Auth] ⚠️  No token, returning null');
            return null;
        }

        try {
            const decoded = jwtDecode<UserInfo>(token);
            console.log('[Auth] ✅ JWT decoded successfully');
            console.log('[Auth] Decoded fields:', Object.keys(decoded));
            console.log('[Auth] User role:', decoded.role);
            console.log('[Auth] User isEnabled:', (decoded as any).isEnabled);

            // Role-based JWT field validation (different roles have different required fields)
            const roleSpecificFields: Record<string, string[]> = {
                ADMIN: ['userId', 'role', 'sub', 'isEnabled', 'isApproved', 'nume'],
                DBEST: ['userId', 'role', 'sub', 'isEnabled', 'isApproved', 'nume'],
                LOGISTIC: ['userId', 'role', 'sub', 'isEnabled', 'nume'],
                PROFESOR: ['userId', 'role', 'sub', 'nume', 'codProfesor', 'points', 'codSe', 'isApproved', 'schoolUpdatedAt'],
                ELEV: ['userId', 'role', 'sub', 'nume', 'isEnabled', 'codSe', 'points', 'schoolId', 'editionId', 'companyId', 'isApproved'],
            };

            const requiredFields = roleSpecificFields[decoded.role as string] || ['userId', 'role', 'nume'];
            const missingFields = requiredFields.filter(field => !(field in decoded));

            if (missingFields.length > 0) {
                console.warn('[Auth] ⚠️  JWT missing required fields for role', decoded.role, ':', missingFields);
                console.warn('[Auth] This may indicate an old token. Please log out and log in again.');
            }

            console.log('[Auth] ✅ Returning decoded userInfo');
            console.log('=== USERINFO MEMO COMPLETE ===');
            return decoded;
        } catch (error) {
            console.error('[Auth] ❌ Failed to decode JWT token:', error);
            return null;
        }
    }, [token]);

    const { ROLES, PATHS, PAGES, getUserHomePage, NAV_LINKS_V2, getListsOfPagePathsGroupedByType } =
        useRoutesProvider();

    const { pathname } = useLocation();
    const navigate = useNavigate();

    // Helper: Validate token (combines isTokenValid + isTokenExpired checks)
    const isValidToken = useCallback((tokenToCheck: string | null): boolean => {
        if (!tokenToCheck) return false;
        return isTokenValid(tokenToCheck) && !isTokenExpired(tokenToCheck);
    }, [isTokenValid, isTokenExpired]);

    const logout = useCallback(() => {
        try {
            setToken(null);
            setRefreshToken(null);
            setTokenInStorage(TOKEN_KEY, "");
            setTokenInStorage(REFRESH_TOKEN_KEY, "");
            setTokenInStorage(TOKEN_EXPIRES_AT_KEY, "");

            // Navigate to login page after logout
            navigate(PATHS[PAGES.LOGIN], { replace: true });
        } catch (error) {
            console.error("Error when performing a logout:", error);
        }
    }, [navigate, setTokenInStorage]);

    // Refresh esuat in client.ts (in afara React-ului) -> logout real, cu navigare la login.
    // Inainte client.ts stergea doar localStorage, iar state-ul React ramanea cu tokenul vechi (2.15).
    useEffect(() => {
        return onSessionExpired(() => {
            toastOneInfo("Sesiunea dumneavoastră a expirat. Vă rugăm să vă conectați încă o dată.");
            logout();
        });
    }, [logout]);

    // Sync authenticated user identity to Application Insights telemetry.
    // No-op in dev/local or when SDK not initialized.
    useEffect(() => {
        if (userInfo?.userId) {
            setAuthenticatedUser(String(userInfo.userId), userInfo.role);
            // Doar id-ul intern si atribute de segmentare - niciodata email/nume (vezi posthog.ts).
            identifyPostHogUser(String(userInfo.userId), {
                role: userInfo.role,
                editionId: userInfo.editionId,
                schoolId: userInfo.schoolId != null ? String(userInfo.schoolId) : undefined,
            });
            if (userInfo.role === 'PROFESOR' && userInfo.schoolApprovedForCurrentEdition === true) {
                marcheazaAprobareaVazuta(String(userInfo.userId), userInfo.editionId);
            }
        } else {
            clearAuthenticatedUser();
            resetPostHogUser();
        }
    }, [userInfo?.userId, userInfo?.role, userInfo?.editionId, userInfo?.schoolId, userInfo?.schoolApprovedForCurrentEdition]);

    // Clean up legacy localStorage keys on mount (from pre-JWT-only architecture)
    useEffect(() => {
        const legacyKeys = ['user', 'profile', 'profileData', 'userData', 'authUser'];
        let cleaned = false;

        legacyKeys.forEach(key => {
            if (localStorage.getItem(key)) {
                localStorage.removeItem(key);
                cleaned = true;
            }
        });

        if (cleaned) {
            console.log('[Auth] Cleaned up legacy localStorage keys from pre-JWT-only architecture');
        }
    }, []); // Run only once on mount

    // Redirectul post-autentificare delega la aceeasi functie pura ca `useAuthRedirects` si
    // `useLoginWithForm`. Inainte avea stagii proprii, cu ALTE tinte decat celelalte doua locuri
    // (ex.: profesor fara formular -> SCHOOL_FORM_ACCOUNT_UNAUTHORIZED_V2 in loc de
    // SCHOOL_REQUEST_FORM), deci unde ajungeai depindea de cine apuca sa navigheze primul.
    // `pathname: null` = intoarce mereu o tinta, e un redirect cerut explicit.
    const performAuthorizedRedirect = useCallback(() => {
        if (!userInfo?.role) {
            return navigate(PATHS[PAGES.LOGIN]);
        }

        const target = resolvePostAuthTarget(userInfo, null, {
            PATHS,
            PAGES,
            ROLES,
            getUserHomePage,
            pasiInscriere: pasiInscriereActive(),
        });
        // `replace: true`: `pathname` e in deps-urile efectului de mai jos, deci asta ruleaza la
        // ORICE navigare intr-o cale interzisa rolului, nu doar o data, la montare. Un `push` ar
        // lasa intrarea interzisa in istoric, inaintea celei push-uite: Back -> cale interzisa ->
        // providerul push-uieste din nou home -> Back devine o capcana.
        return navigate(target ?? getUserHomePage(userInfo.role), { replace: true });
    }, [userInfo, navigate, ROLES, PATHS, PAGES, getUserHomePage]);

    const performRedirect = useCallback(() => {
        if (!isValidToken(token)) {
            logout();
            navigate(PATHS[PAGES.LOGIN]);
        } else {
            performAuthorizedRedirect();
        }
    }, [token, isValidToken, logout, navigate, PATHS, PAGES, performAuthorizedRedirect]);

    const updateRefreshToken = useCallback((newToken: string) => {
        try {
            if (!newToken) return logout();
            setRefreshToken(newToken);
            setTokenInStorage(REFRESH_TOKEN_KEY, newToken);
        } catch (error) {
            console.error("Error when updating the refreshToken:", error);
        }
    }, [logout, setTokenInStorage]);

    const updateToken = useCallback((newToken: string) => {
        console.log('=== UPDATE TOKEN START ===');
        console.log('[Auth] updateToken called with token (first 50 chars):', newToken.substring(0, 50));

        if (!newToken || !isValidToken(newToken)) {
            console.log('[Auth] ❌ Invalid token, logging out');
            return logout();
        }

        console.log('[Auth] Calling setToken() - React state update scheduled');
        setToken(newToken);  // ← This is ASYNC!
        console.log('[Auth] setToken() called (state update pending...)');

        console.log('[Auth] Saving token to localStorage');
        // Odata cu tokenul se scrie si expirarea lui: fara ea, reimprospatarea proactiva din
        // client.ts nu pornea deloc in prima ora de dupa login (core/auth/expirare-token.ts).
        salveazaTokenDeAcces(newToken);
        console.log('[Auth] ✅ Token saved to localStorage');

        console.log('=== UPDATE TOKEN END (React state update still pending!) ===');
    }, [logout, isValidToken]);

    // Gardele pe rol, la FIECARE navigare — nu doar la schimbarea tokenului (2.16).
    // Inainte efectul depindea doar de [token, refreshToken], deci un link intern spre o ruta
    // interzisa rolului randa pagina; verificarea se facea doar la deep-link sau la reload.
    // ALLOWED_ROLES era altfel folosit doar cosmetic, ca sa ascunda linkuri din sidebar.
    function validateTokenChange() {
        try {
            const { unsecuredPaths } = getListsOfPagePathsGroupedByType();
            const isUnsecuredPath = unsecuredPaths.some((path) => matchPath(path, pathname));

            // No tokens - redirect if on secured path
            if (!token && !refreshToken && !isUnsecuredPath) {
                performRedirect();
                return;
            }

            // Has tokens - validate permission for current page.
            // Rutele publice NU se redirecteaza de aici: le trateaza UnauthenticatedLayout,
            // cu acelasi resolver — altfel ar fi doi decidenti pe aceeasi navigare.
            if (token && refreshToken && userInfo) {
                if (!isPathAllowedForRole(pathname, userInfo.role, NAV_LINKS_V2, {
                    needsSchoolRegistrationConfirmation: userInfo.needsSchoolRegistrationConfirmation,
                    confirmationPath: PATHS[PAGES.SCHOOL_REGISTRATION_CONFIRMATION],
                })) {
                    performRedirect();
                }
            }
        } catch (error) {
            console.error("Error validating token change:", error);
        }
    }

    const handleTokenRefresh = useCallback(async (forced = false): Promise<string | undefined> => {
        try {
            const { unsecuredPaths } = getListsOfPagePathsGroupedByType();
            if (unsecuredPaths.some((path) => matchPath(path, pathname))) {
                return undefined;
            }

            if (!token || !refreshToken) {
                logout();
                return undefined;
            }

            const decodedToken = jwtDecode(token);
            if (!decodedToken || !decodedToken.exp) {
                logout();
                return undefined;
            }

            const currentTime = Math.floor(Date.now() / 1000);
            const tokenTimeRemaining = decodedToken.exp - currentTime;

            if (tokenTimeRemaining > 120 && !forced) {
                return undefined;
            }

            // 2026-09-28: aceeasi functie ca interceptorul din client.ts, nu un POST propriu. Doua cai
            // independente trimiteau acelasi refresh token simultan, iar cea care pierdea cursa facea
            // logout (core/auth/refresh-serializat.ts). Functia comuna scrie deja ambele tokenuri si
            // expirarea in localStorage; aici doar se aduce starea React la zi.
            const newToken = await refreshAccessToken();
            updateToken(newToken);
            updateRefreshToken(localStorage.getItem(REFRESH_TOKEN_KEY) ?? "");

            return newToken;
        } catch (error) {
            if (import.meta.env.DEV) {
                console.error('[Auth] Error refreshing token:', error);
            }
            toastOneInfo("Sesiunea dumneavoastră a expirat. Vă rugăm să vă conectați încă o dată.");
            logout();
            return undefined;
        }
    }, [token, refreshToken, pathname, getListsOfPagePathsGroupedByType, logout, updateToken, updateRefreshToken]);

    // Token refresh interval removed - now handled exclusively by axios interceptor in client.ts
    // Manual refresh still available via handleTokenRefresh(forced=true) for critical operations
    // Cross-tab storage sync removed - tokens are managed per-tab, simplifies state management

    useEffect(() => {
        validateTokenChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, refreshToken, pathname]);

    const authProviderValue = useMemo(() => ({
        token,
        logout,
        userInfo,
        updateToken,
        refreshToken,
        performRedirect,
        updateRefreshToken,
        handleTokenRefresh,
    }), [
        token,
        logout,
        userInfo,
        updateToken,
        refreshToken,
        performRedirect,
        updateRefreshToken,
        handleTokenRefresh,
    ]);

    return <Provider value={authProviderValue}>{children}</Provider>;
}

export { AuthProviderContextV2, AuthProviderV2 };
